import React, { useEffect, useState } from "react"
import { Link } from "gatsby"
import Ample from "../components/ample/ample"
import ExplainerPopover from "../components/general/explainerpopover"
import TradingViewMarketOverview from "../components/tradingview/marketoverview"
import TradingViewMostActive from "../components/tradingview/mostactive"
import ChatIcon from '@mui/icons-material/Chat'
import { TOOLS, getToolIcon, getToolLink, getToolPageTitle } from "../optiontools"
import "../css/icons/font-awesome/css/fontawesome.css"
import "../css/icons/font-awesome/css/fa-solid.css"

const IndexPage = (props) => {
  const pc_chart_url=`${process.env.GATSBY_CONTENT_SITE_URL}/plots/pc_ratio.html`;
  const seoDescription="Free simple tools for the active trader. Put/Call ratio, max pain, option pricer, spread finder tool.";
  
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      tool: TOOLS.TICKER_REPORT,
      description: "Real-time stock charts, company fundamentals, and social media buzz in one comprehensive view.",
      color: "#2196F3"
    },
    {
      tool: TOOLS.MAX_PAIN,
      description: "Discover where stock prices might get pinned at expiration based on maximum option seller profit.",
      color: "#FF9800"
    },
    {
      tool: TOOLS.OPTION_PRICER,
      description: "Plot how option prices change with stock price movements. Black-Scholes calculator with visual analysis.",
      color: "#9C27B0"
    },
    {
      tool: TOOLS.SPREAD_FINDER,
      description: "Find and compare vertical option spreads with profit/loss zones mapped to historical prices.",
      color: "#F44336"
    },
    {
      tool: TOOLS.PUT_CALL_RATIOS,
      description: "Track market sentiment through equity-only put/call ratio. Identify potential market turning points.",
      color: "#00BCD4"
    },
    {
      tool: TOOLS.CHAT,
      description: "AI-powered options Q&A. Get instant answers about market positioning, max pain, and strategy outcomes.",
      color: "#4CAF50"
    }
  ];

  const stats = [
    { value: "6+", label: "Tools" },
    { value: "Real-time", label: "Market Data" },
    { value: "AI-Powered", label: "Analysis" },
    { value: "Free", label: "to Start" }
  ];

  return (
    <Ample currPageTitle={getToolPageTitle(TOOLS.MARKET_OVERVIEW)} seoDescription={seoDescription} location={props.location.href}>
      <style>{`
        .hero-section {
          text-align: center;
          padding: 60px 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 12px;
          margin-bottom: 40px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        }
        
        .hero-title {
          font-size: 2.8rem;
          font-weight: 700;
          margin-bottom: 20px;
          line-height: 1.2;
        }
        
        .hero-subtitle {
          font-size: 1.3rem;
          opacity: 0.95;
          max-width: 700px;
          margin: 0 auto 30px;
          font-weight: 300;
        }
        
        .stats-container {
          display: flex;
          justify-content: center;
          gap: 40px;
          flex-wrap: wrap;
          margin-top: 40px;
        }
        
        .stat-item {
          text-align: center;
        }
        
        .stat-value {
          font-size: 2.5rem;
          font-weight: 700;
          display: block;
          margin-bottom: 5px;
        }
        
        .stat-label {
          font-size: 0.95rem;
          opacity: 0.9;
          font-weight: 400;
        }
        
        .features-section {
          margin: 60px 0;
        }
        
        .section-title {
          text-align: center;
          font-size: 2.2rem;
          font-weight: 600;
          margin-bottom: 15px;
          color: #333;
        }
        
        .section-subtitle {
          text-align: center;
          font-size: 1.1rem;
          color: #666;
          margin-bottom: 50px;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }
        
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 30px;
          margin-bottom: 60px;
        }
        
        .feature-card {
          background: white;
          border-radius: 12px;
          padding: 35px 25px;
          text-align: center;
          transition: all 0.3s ease;
          border: 2px solid #f0f0f0;
          cursor: pointer;
          text-decoration: none;
          color: inherit;
          display: block;
          box-shadow: 0 4px 6px rgba(0,0,0,0.05);
        }
        
        .feature-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.15);
          text-decoration: none;
          color: inherit;
        }
        
        .feature-icon {
          margin-bottom: 20px;
          transition: transform 0.3s ease;
        }
        
        .feature-card:hover .feature-icon {
          transform: scale(1.1);
        }
        
        .feature-title {
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 15px;
          color: #333;
        }
        
        .feature-description {
          font-size: 1rem;
          color: #666;
          line-height: 1.6;
        }
        
        .market-widgets {
          margin: 60px 0;
        }
        
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        
        .animate-on-scroll.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        .cta-button {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 14px 32px;
          background: white;
          color: #667eea;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1.1rem;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        
        .cta-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.25);
          text-decoration: none;
          color: #667eea;
        }
        
        .cta-button:hover .cta-icon {
          transform: scale(1.1);
        }
        
        .cta-icon {
          transition: transform 0.3s ease;
        }

        @media (max-width: 768px) {
          .hero-title {
            font-size: 2rem;
          }
          .hero-subtitle {
            font-size: 1.1rem;
          }
          .features-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Hero Section */}
      <div className="hero-section">
        <h1 className="hero-title">
          Free simple tools for the active trader
        </h1>
        <p className="hero-subtitle">
          Analyze market conditions, analyze option strategies, and find your edge.
        </p>
        <Link to="/chat/" className="cta-button">
          <ChatIcon className="cta-icon" style={{ fontSize: 24 }} />
          Try AI Options Chat
        </Link>
        
        <div className="stats-container">
          {stats.map((stat, idx) => (
            <div key={idx} className="stat-item">
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div className="features-section">
        <h2 className="section-title">Tools for Options Traders</h2>
        
        <div className="features-grid">
          {features.map((feature, idx) => {
            const icon = getToolIcon(feature.tool);
            const link = getToolLink(feature.tool);
            const toolName = getToolPageTitle(feature.tool);
            // Clone icon with consistent sizing for both MUI and FontAwesome
            const styledIcon = React.cloneElement(icon, {
              style: { ...icon.props.style, fontSize: 48 }
            });
            return (
              <Link 
                key={idx} 
                to={link} 
                className={`feature-card animate-on-scroll ${isVisible[`feature-${idx}`] ? 'visible' : ''}`}
                id={`feature-${idx}`}
              >
                <div className="feature-icon" style={{ color: feature.color }}>
                  {styledIcon}
                </div>
                <h3 className="feature-title">{toolName}</h3>
                <p className="feature-description">{feature.description}</p>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Market Data Section */}
      <div className="market-widgets">
        <h2 className="section-title">Live Market Overview</h2>
        <p className="section-subtitle">
          Stay updated with real-time market data and trending securities.
        </p>
        
        <div className="row" style={{ marginBottom: "40px" }}>
          <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12" style={{minWidth:"380px",maxWidth:"600px", margin: "0 auto", marginBottom: "20px"}}>
            <div className={`card animate-on-scroll ${isVisible['market-1'] ? 'visible' : ''}`} id="market-1">
              <div className="card-body">
                <h4 className="card-title">Major Indexes</h4>
                <TradingViewMarketOverview />
              </div>
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12" style={{minWidth:"380px",maxWidth:"600px", margin: "0 auto", marginBottom: "20px"}}>
            <div className={`card animate-on-scroll ${isVisible['market-2'] ? 'visible' : ''}`} id="market-2">
              <div className="card-body">
                <h4 className="card-title">Today's Most Active</h4>
                <TradingViewMostActive/>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-12 col-sm-12 col-xs-12">
            <div className={`card animate-on-scroll ${isVisible['pcratios'] ? 'visible' : ''}`} id="pcratios">
              <div className="card-body">
                <h4 className="card-title">
                  Equity-only Put/Call Ratios  
                  <ExplainerPopover>
                    Here we chart the ratio of open option interest in puts vs. calls (a measure of the amount of caution in the market). It is sometimes the case that when this ratio's trendline 'rolls over' and starts a new trend, the overall stock market soon follows.
                  </ExplainerPopover>
                </h4>
                <h6 className="card-subtitle" style={{ marginBottom: "20px", color: "#666" }}>
                  More detailed analysis and comparison to S&P 500 index available at <Link to="/pcratios/">PC Ratios page</Link>
                </h6>
                <iframe 
                  src={pc_chart_url}
                  style={{border:"0", width: "100%", height: "400px"}}
                  title="Put/Call Ratios">
                </iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Ample>
  );
};
export default IndexPage;